/**
 * Demo-only variant/size-chart builders for fashion preview catalogs.
 * Skips products that already declare variants (backward compatible).
 */
import type { Product, ProductOption, ProductVariant } from '@/services/products'

export type DemoSizeChart = {
  type: 'clothing' | 'shoes'
  values: Record<string, string>[]
}

const ALPHA_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'] as const
const SHOE_SIZES = ['34', '35', '36', '37', '38', '39', '40'] as const

function optionRow(productId: string, optIdx: number, name: string, values: string[]): ProductOption {
  const oid = `${productId}-opt-${optIdx}-${name.replace(/\s+/g, '-')}`
  return {
    id: oid,
    product_id: productId,
    name,
    position: optIdx,
    values: values.map((value, i) => ({
      id: `${oid}-val-${i}`,
      option_id: oid,
      value,
      position: i,
    })),
  }
}

function mkVariant(
  productId: string,
  suffix: string,
  attributes: Record<string, string>,
  o: {
    price: number
    compare_at_price?: number | null
    inventory_quantity: number
    status: ProductVariant['status']
    position: number
    image_url?: string | null
    is_default?: boolean
  }
): ProductVariant {
  return {
    id: `${productId}-var-${suffix}`,
    product_id: productId,
    sku: `SKU-${suffix}`.slice(0, 48),
    price: o.price,
    stock: o.inventory_quantity,
    inventory_quantity: o.inventory_quantity,
    position: o.position,
    status: o.status,
    deleted_at: null,
    attributes,
    compare_at_price: o.compare_at_price ?? null,
    image_url: o.image_url ?? null,
    is_default: o.is_default === true,
  }
}

function clothingSizeChart(): DemoSizeChart {
  return {
    type: 'clothing',
    values: [
      { size: 'XS', chest_cm: '82-86', waist_cm: '66-70', eu: '34' },
      { size: 'S', chest_cm: '86-90', waist_cm: '70-74', eu: '36' },
      { size: 'M', chest_cm: '90-94', waist_cm: '74-78', eu: '38' },
      { size: 'L', chest_cm: '94-98', waist_cm: '78-82', eu: '40' },
      { size: 'XL', chest_cm: '98-102', waist_cm: '82-86', eu: '42' },
      { size: 'XXL', chest_cm: '102-106', waist_cm: '86-90', eu: '44' },
    ],
  }
}

function shoesSizeChart(): DemoSizeChart {
  return {
    type: 'shoes',
    values: [
      { eu: '34', foot_cm: '22.0' },
      { eu: '35', foot_cm: '22.4' },
      { eu: '36', foot_cm: '23.0' },
      { eu: '37', foot_cm: '23.6' },
      { eu: '38', foot_cm: '24.3' },
      { eu: '39', foot_cm: '25.0' },
      { eu: '40', foot_cm: '25.6' },
    ],
  }
}

function variantsAlphaOnly(
  productId: string,
  basePrice: number,
  baseCompare: number | null,
  imgs: string[]
): { variants: ProductVariant[]; options: ProductOption[]; size_chart: DemoSizeChart } {
  const img0 = imgs[0] ?? ''
  const img1 = imgs[1] ?? img0
  const variants: ProductVariant[] = ALPHA_SIZES.map((size, i) => {
    const oos = size === 'XXL' || size === 'XS'
    const price = basePrice + (i % 3) * 4
    const cap = baseCompare != null ? baseCompare + i * 2 : null
    return mkVariant(
      productId,
      `sz-${size}`,
      { Size: size },
      {
        price,
        compare_at_price: cap,
        inventory_quantity: oos ? 0 : 6 + i,
        status: oos ? 'out_of_stock' : 'active',
        position: i,
        image_url: i % 2 === 1 ? img1 : img0,
        is_default: size === 'M',
      }
    )
  })
  return {
    variants,
    options: [optionRow(productId, 0, 'Size', [...ALPHA_SIZES])],
    size_chart: clothingSizeChart(),
  }
}

function variantsShoes(
  productId: string,
  basePrice: number,
  baseCompare: number | null,
  imgs: string[]
): { variants: ProductVariant[]; options: ProductOption[]; size_chart: DemoSizeChart } {
  const img0 = imgs[0] ?? ''
  const img1 = imgs[1] ?? img0
  const variants: ProductVariant[] = SHOE_SIZES.map((size, i) => {
    const oos = size === '37' || size === '40'
    const price = basePrice + i * 3
    const cap = baseCompare != null ? baseCompare + 15 + i * 2 : basePrice + 40 + i * 2
    return mkVariant(
      productId,
      `eu-${size}`,
      { Size: size },
      {
        price,
        compare_at_price: cap,
        inventory_quantity: oos ? 0 : 4,
        status: oos ? 'out_of_stock' : 'active',
        position: i,
        image_url: i % 2 === 1 ? img1 : img0,
        is_default: size === '39',
      }
    )
  })
  return {
    variants,
    options: [optionRow(productId, 0, 'Size', [...SHOE_SIZES])],
    size_chart: shoesSizeChart(),
  }
}

function variantsColorsOnly(
  productId: string,
  colors: string[],
  basePrice: number,
  baseCompare: number | null,
  imgs: string[]
): { variants: ProductVariant[]; options: ProductOption[]; size_chart: DemoSizeChart } {
  const img0 = imgs[0] ?? ''
  const img1 = imgs[1] ?? img0
  const variants: ProductVariant[] = colors.map((color, i) => {
    const oos = color === colors[colors.length - 1]
    const price = basePrice + i * 12
    return mkVariant(
      productId,
      `c-${i}`,
      { Color: color },
      {
        price,
        compare_at_price: baseCompare != null ? baseCompare + i * 5 : null,
        inventory_quantity: oos ? 0 : 8,
        status: oos ? 'out_of_stock' : 'active',
        position: i,
        image_url: i > 0 ? img1 : img0,
        is_default: i === 0,
      }
    )
  })
  return {
    variants,
    options: [optionRow(productId, 0, 'Color', colors)],
    size_chart: clothingSizeChart(),
  }
}

function variantsColorSizeGrid(
  productId: string,
  colors: string[],
  sizes: readonly string[],
  basePrice: number,
  baseCompare: number | null,
  imgs: string[]
): { variants: ProductVariant[]; options: ProductOption[]; size_chart: DemoSizeChart } {
  const img0 = imgs[0] ?? ''
  const img1 = imgs[1] ?? img0
  const variants: ProductVariant[] = []
  let pos = 0
  for (const color of colors) {
    for (const size of sizes) {
      const slug = `${pos}-${color}-${size}`.replace(/\s+/g, '')
      const oos = (color === colors[0] && size === 'XXL') || (color === colors[1] && size === 'XS')
      const price = basePrice + pos * 3
      const cap = baseCompare != null ? baseCompare + pos * 2 : null
      variants.push(
        mkVariant(
          productId,
          slug,
          { Color: color, Size: size },
          {
            price,
            compare_at_price: cap,
            inventory_quantity: oos ? 0 : 5,
            status: oos ? 'out_of_stock' : 'active',
            position: pos,
            image_url: pos % 3 === 0 ? img0 : img1,
            is_default: color === colors[0] && size === 'M',
          }
        )
      )
      pos++
    }
  }
  return {
    variants,
    options: [
      optionRow(productId, 0, 'Color', colors),
      optionRow(productId, 1, 'Size', [...sizes]),
    ],
    size_chart: clothingSizeChart(),
  }
}

function aggregateVariantProduct(p: Product, variants: ProductVariant[]): Partial<Product> {
  const prices = variants.map((v) => v.price).filter((x): x is number => x != null && Number.isFinite(x))
  const minP = prices.length ? Math.min(...prices) : p.price
  const maxP = prices.length ? Math.max(...prices) : p.price
  const totalStock = variants.reduce((s, v) => s + (v.inventory_quantity ?? v.stock ?? 0), 0)
  const def = variants.find((v) => v.is_default) ?? variants.find((v) => v.status === 'active') ?? variants[0]
  return {
    has_variants: true,
    variants,
    variant_count: variants.length,
    min_price: minP,
    max_price: maxP,
    total_stock: totalStock,
    stock: totalStock,
    default_variant_id: def?.id ?? null,
    price: minP,
  }
}

/**
 * Enriches fashion preview products with Shopify-style variants + size chart when missing.
 */
export function enrichFashionVerticalProduct(p: Product): Product {
  if (p.has_variants === true && p.variants != null && p.variants.length > 0) {
    return p
  }

  const altGallery = '/templates/fashion-v2-gen/fv2-gen-prod-blazer-ivoire.webp'
  const imgs = [...(p.images?.length ? p.images : []), p.cover_image, p.image].filter(
    (x): x is string => typeof x === 'string' && x.length > 0
  )
  const uniq = [...new Set(imgs)]
  if (uniq.length < 2) uniq.push(altGallery)

  const slug = p.slug ?? ''

  let built: { variants: ProductVariant[]; options: ProductOption[]; size_chart: DemoSizeChart }

  if (slug === 'sneakers-cuir-craie') {
    built = variantsShoes(p.id, p.price, p.compare_at_price ?? null, uniq)
  } else if (slug === 'sac-bandouliere-cuir') {
    built = variantsColorsOnly(p.id, ['Noir', 'Cognac', 'Miel'], p.price, p.compare_at_price ?? null, uniq)
  } else if (slug === 'manteau-laine-minuit') {
    built = variantsColorSizeGrid(p.id, ['Noir', 'Gris'], ALPHA_SIZES, p.price, p.compare_at_price ?? null, uniq)
  } else {
    built = variantsAlphaOnly(p.id, p.price, p.compare_at_price ?? null, uniq)
  }

  const agg = aggregateVariantProduct(p, built.variants)
  return {
    ...p,
    ...agg,
    options: built.options,
    size_chart: built.size_chart,
  }
}
